// src/components/ThreeModelViewer.js

import React, { useRef, useEffect } from "react";
import * as THREE from "three";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader.js";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader.js";
import { OrbitControls } from "three/examples/jsm/controls/OrbitControls.js";

function ThreeModelViewer({ modelUrl }) {
  const mountRef = useRef(null);

  useEffect(() => {
    if (!modelUrl) return;

    console.log("ThreeModelViewer: Attempting to load model from:", modelUrl);

    // Grab the container <div> we'll render into
    const container = mountRef.current;
    const { width, height } = container.getBoundingClientRect();

    // 1. Scene, camera
    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(75, width / height, 0.1, 1000);

    // Original: camera.position.set(0, 1, 3);
    // Use Potree's camera position
    camera.position.set(
      0.06639350496362777,
      -1.0497696552474542,
      -0.6670040499676613
    );

    // 2. Renderer
    const renderer = new THREE.WebGLRenderer({ antialias: true });
    renderer.setSize(width, height);
    container.appendChild(renderer.domElement);

    // 3. Lighting
    const ambientLight = new THREE.AmbientLight(0xffffff, 1);
    scene.add(ambientLight);

    // 4. GLTFLoader + DRACOLoader
    const loader = new GLTFLoader();
    const dracoLoader = new DRACOLoader();
    dracoLoader.setDecoderPath("/draco/");
    loader.setDRACOLoader(dracoLoader);

    loader.load(
      modelUrl,
      (gltf) => {
        console.log("ThreeModelViewer: Model loaded successfully!");
        scene.add(gltf.scene);

        // OPTIONAL: bounding-box approach or other camera logic
        // to ensure the model is actually visible at that camera position.
      },
      undefined,
      (error) => {
        console.error("ThreeModelViewer: Error loading glb model:", error);
      }
    );

    // 5. OrbitControls
    const controls = new OrbitControls(camera, renderer.domElement);
    controls.enableDamping = true;
    controls.dampingFactor = 0.1;
    controls.rotateSpeed = 1.0;
    controls.zoomSpeed = 1.2;
    controls.panSpeed = 1.2;
    controls.minDistance = 0.5;
    controls.maxDistance = 50;

    // Update OrbitControls target to Potree's "view.target"
    controls.target.set(
      -0.18462442833566806,
      -0.4950994546584627,
      0.22704711182423365
    );
    // Force camera to look at that same point
    camera.lookAt(controls.target);
    controls.update();

    // 6. Render loop
    const animate = () => {
      requestAnimationFrame(animate);
      controls.update();
      renderer.render(scene, camera);
    };
    animate();

    // 7. Handle window resize
    const handleResize = () => {
      const { width, height } = container.getBoundingClientRect();
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener("resize", handleResize);

    // Cleanup on unmount
    return () => {
      window.removeEventListener("resize", handleResize);
      if (container.contains(renderer.domElement)) {
        container.removeChild(renderer.domElement);
      }
      renderer.dispose();
    };
  }, [modelUrl]);

  // Use the same styling as your iFrame slides:
  // a parent container with paddingBottom for 16:9 aspect ratio.
  return (
    <div style={{ padding: "0 1rem" }}>
      <h4 style={{ marginBottom: "0.5rem", textAlign: "left" }}>3D Model</h4>
      <div
        style={{
          position: "relative",
          width: "100%",
          paddingBottom: "28.12%", // ~16:9 ratio
        }}
      >
        <div
          ref={mountRef}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            width: "100%",
            height: "100%",
            overflow: "hidden",
          }}
        />
      </div>
    </div>
  );
}

export default ThreeModelViewer;
